import { useMutation } from '@apollo/client'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import { PageHeader } from 'components/page-header'
import { FormActionBar } from 'components/form-util'
import { useIsMobile } from 'lib/hooks'
import { useHistory, useRouteMatch } from 'react-router-dom'
import * as yup from 'yup'
import {
  UPDATE_STUDENT,
  UPSERT_MEDICAL_PROFILE,
} from 'pages/pga-coach/students/student-edit-page/api-hooks'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import { useSnackbar } from 'notistack'
import FormContent from 'pages/pga-coach/students/student-edit-page/form-container'
import {
  AgeTiles,
  ContactInfoFields,
  GuardianNameFields,
  StudentNameFields,
} from 'pages/pga-coach/students/student-edit-page/form-fields'
import useStyles from 'pages/pga-coach/students/student-edit-page/styles'
import { gtmEvent } from 'lib/gtm'
import { stripPhoneNumber } from 'utils/phoneNumber'
import MedicalProfile from './medical-profile'

const validationSchema = yup.object().shape({
  firstName: yup
    .string()
    .min(1)
    .required(),
  lastName: yup
    .string()
    .min(1)
    .required(),
  email: yup
    .string()
    .email()
    .required(),
  phone: yup.string().matches(/^\d{3}-?\d{3}-?\d{4}$/, {
    message: 'Phone number must be 10 digits',
    excludeEmptyString: true,
  }),
  guardianFirstName: yup.string().min(1),
  guardianLastName: yup.string().min(1),
  medicalProfile: yup.object().shape({
    info: yup.string(),
  }),
})

const cleanupPhoneNumber = num => {
  if (num) {
    const cleanedNum = num.replace(/\D/g, '')
    return `${cleanedNum}`
  }
  return null
}

export default function EditStudentForm({ student }) {
  const isMobile = useIsMobile()
  const classes = useStyles()
  const { path } = useRouteMatch()
  const history = useHistory()
  const { enqueueSnackbar } = useSnackbar()

  const studentName = `${student?.firstName} ${student?.lastName}`
  const isMinor = student.isMinor
  const { contact = {} } = student

  const [updateStudent, { loading }] = useMutation(UPDATE_STUDENT)
  const [
    upsertMedicalProfile,
    { loading: upsertingMedicalProfile },
  ] = useMutation(UPSERT_MEDICAL_PROFILE)

  const form = useForm({
    defaultValues: {
      firstName: student.firstName || '',
      lastName: student.lastName || '',
      email: contact?.email || '',
      phone: stripPhoneNumber(contact?.phone) || '',
      guardianFirstName: contact?.firstName,
      guardianLastName: contact?.lastName,
      medicalProfile: {
        info: student.medicalProfile?.info || '',
      },
    },
    resolver: yupResolver(validationSchema),
  })

  const { handleSubmit, formState } = form
  const { dirtyFields } = formState

  const handleResponses = (
    updateStudentResponse,
    upsertMedicalProfileResponse,
  ) => {
    const updateStudentSuccess =
      updateStudentResponse.data?.updateStudent.success
    const upsertMedicalProfileSuccess =
      upsertMedicalProfileResponse.data?.upsertMedicalProfile?.id

    if (updateStudentSuccess && upsertMedicalProfileSuccess) {
      gtmEvent({
        event: 'formSubmit',
        formCategory: 'edit-student',
        formAction: 'student-edited-student-details',
        metaData: dirtyFields,
      })
      enqueueSnackbar('Account updated', {
        variant: 'success',
      })
      history.push(url)
    } else {
      const errorMessage = []
      if (!updateStudentSuccess) {
        errorMessage.push(updateStudentResponse.data?.updateStudent.message)
      }
      if (!upsertMedicalProfileSuccess) {
        errorMessage.push('Allergies and Medications could not be updated')
      }
      enqueueSnackbar(errorMessage.join('. '), { variant: 'error' })
    }
  }

  const onSubmit = async formValues => {
    formValues.phone = cleanupPhoneNumber(formValues.phone)
    const { medicalProfile, ...updateStudentValues } = formValues
    try {
      const updateStudentResponse = await updateStudent({
        variables: { studentId: student.id, inputs: updateStudentValues },
      })

      const upsertMedicalProfileResponse = await upsertMedicalProfile({
        variables: { studentId: student.id, inputs: formValues.medicalProfile },
      })

      handleResponses(updateStudentResponse, upsertMedicalProfileResponse)
    } catch (e) {
      enqueueSnackbar('Sorry, the account could not be updated', {
        variant: 'error',
      })
    }
  }

  const url = `${path
    .split('/')
    .slice(0, 3)
    .join('/')}/${student.id}`

  const crumbs = [
    {
      label: 'Accounts',
      to: `${path
        .split('/')
        .slice(0, 2)
        .join('/')}/students`,
    },
    {
      label: studentName,
      to: url,
    },
  ]

  return (
    <Box className={classes.root}>
      <PageHeader
        title="Edit Account"
        crumbs={crumbs}
        backTo={url}
        isMobile={isMobile}
      />
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormActionBar
            includeCancel
            onCancel={() => history.push(url)}
            stickToBottom={isMobile}
            loading={loading || upsertingMedicalProfile}
          >
            <Button variant="contained" color="primary" type="submit">
              Save
            </Button>
          </FormActionBar>
          <Box className={classes.formContainer}>
            <FormContent eyebrow="Participant information">
              <AgeTiles isMinor={isMinor} />
              <Box className={classes.formFieldsSection}>
                <StudentNameFields />
                {!isMinor && <ContactInfoFields />}
                {isMinor && (
                  <>
                    <Typography className={classes.sectionLabel}>
                      Guardian Information
                    </Typography>
                    <GuardianNameFields />
                    <ContactInfoFields />
                  </>
                )}
              </Box>
            </FormContent>
            <FormContent eyebrow="Allergies and medications">
              <MedicalProfile />
            </FormContent>
          </Box>
        </form>
      </FormProvider>
    </Box>
  )
}
